/**
 * Initialize Content Repository FacetWP controls.
 *
 * @return {void}
 */
export default function () {
    const initialize = () => {
        let scrollAfterPagination = false;

        const updateSearchPlaceholder = () => {
            document.querySelectorAll('.content-repository .facetwp-search').forEach((input) => {
                input.setAttribute('placeholder', 'Search');
            });
        };

        const initializeFilters = () => {
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            document.querySelectorAll('.content-repository__filter').forEach((filter) => {
                const toggle = filter.querySelector('.content-repository__filter-toggle');
                const body = filter.querySelector('.content-repository__filter-body');

                if (!toggle || !body) return;

                toggle.setAttribute('aria-expanded', filter.open ? 'true' : 'false');
                filter.addEventListener('toggle', () => {
                    toggle.setAttribute('aria-expanded', filter.open ? 'true' : 'false');
                });

                toggle.addEventListener('click', (event) => {
                    event.preventDefault();

                    if (filter.dataset.animating === 'true') return;

                    const opening = !filter.open;
                    toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');

                    if (reducedMotion || typeof filter.animate !== 'function') {
                        filter.open = opening;
                        return;
                    }

                    const startHeight = filter.offsetHeight;
                    let endHeight;

                    if (opening) {
                        filter.open = true;
                        endHeight = filter.scrollHeight;

                        const items = body.querySelectorAll(
                            '.facetwp-checkbox, .facetwp-radio, .facetwp-link, .content-repository__filter-clear'
                        );
                        items.forEach((item, index) => {
                            item.animate([
                                { opacity: 0, transform: 'translateY(-5px)' },
                                { opacity: 1, transform: 'translateY(0)' },
                            ], {
                                duration: 220,
                                delay: 70 + (index * 35),
                                easing: 'ease-out',
                                fill: 'both',
                            });
                        });
                    } else {
                        filter.open = false;
                        endHeight = filter.offsetHeight;
                        filter.open = true;
                    }

                    filter.dataset.animating = 'true';
                    filter.style.overflow = 'hidden';
                    const animation = filter.animate([
                        { height: startHeight + 'px' },
                        { height: endHeight + 'px' },
                    ], {
                        duration: 320,
                        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                    });

                    animation.addEventListener('finish', () => {
                        filter.open = opening;
                        filter.style.height = '';
                        filter.style.overflow = '';
                        delete filter.dataset.animating;
                    });
                });
            });
        };

        const updateResultCount = () => {
            if (!window.FWP || !window.FWP.facets || !window.FWP.settings || !window.FWP.settings.pager) return;

            const totalRows = parseInt(window.FWP.settings.pager.total_rows, 10) || 0;
            const label = totalRows === 1 ? 'item found' : 'items found';
            const filterNames = ['foundations', 'futures', 'resource_country', 'resource_type', 'search'];
            const hasActiveFilter = filterNames.some((name) => {
                const value = window.FWP.facets[name];
                return Array.isArray(value) ? value.length > 0 : Boolean(value);
            });

            document.querySelectorAll('.content-repository__result-count').forEach((count) => {
                count.hidden = !hasActiveFilter;
                count.textContent = hasActiveFilter ? totalRows + ' ' + label : '';
            });
        };

        const updateSort = () => {
            document.querySelectorAll('.content-repository__sort .facetwp-sort').forEach((sort) => {
                const select = sort.querySelector('.facetwp-sort-select');

                if (!select) return;

                const existing = sort.querySelector('.content-repository__sort-menu');

                if (existing) existing.remove();

                const menu = document.createElement('div');
                const selected = select.options[select.selectedIndex] || select.options[0];
                menu.className = 'content-repository__sort-menu';
                menu.innerHTML =
                    '<button class="content-repository__sort-trigger" type="button" aria-expanded="false">' +
                    '<span>' + selected.text + '</span>' +
                    '<span class="content-repository__sort-icon" aria-hidden="true"></span>' +
                    '</button>' +
                    '<div class="content-repository__sort-options" hidden></div>';

                const options = menu.querySelector('.content-repository__sort-options');

                Array.prototype.forEach.call(select.options, (option) => {
                    const button = document.createElement('button');
                    button.className = 'content-repository__sort-option';
                    button.type = 'button';
                    button.textContent = option.text;
                    button.dataset.value = option.value;

                    if (option.selected) button.classList.add('is-selected');

                    button.addEventListener('click', () => {
                        select.value = option.value;
                        select.dispatchEvent(new Event('change', { bubbles: true }));
                        menu.querySelector('.content-repository__sort-trigger span').textContent = option.text;
                        menu.classList.remove('is-open');
                        menu.querySelector('.content-repository__sort-trigger').setAttribute('aria-expanded', 'false');
                        options.hidden = true;
                    });

                    options.appendChild(button);
                });

                menu.querySelector('.content-repository__sort-trigger').addEventListener('click', function () {
                    const isOpen = menu.classList.toggle('is-open');
                    this.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                    options.hidden = !isOpen;
                });

                sort.appendChild(menu);
            });
        };

        const updatePager = () => {
            if (!window.FWP || !window.FWP.settings || !window.FWP.settings.pager) return;

            const currentPage = parseInt(window.FWP.settings.pager.page, 10) || 1;
            const totalPages = parseInt(window.FWP.settings.pager.total_pages, 10) || 1;
            const arrow = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
                '<path d="M12 5L19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
                '<path d="M5 12L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>';

            document.querySelectorAll('.content-repository__pager .facetwp-pager').forEach((pager) => {
                let html = '<button class="facetwp-page content-repository__pager-arrow prev" type="button" aria-label="Previous page"';

                html += currentPage > 1 ?
                    ' data-page="' + (currentPage - 1) + '"' :
                    ' disabled aria-disabled="true"';
                html += '>' + arrow + '</button>';

                const visiblePages = [];
                for (let page = 1; page <= Math.min(3, totalPages); page++) {
                    visiblePages.push(page);
                }

                if (!visiblePages.includes(currentPage)) visiblePages.push(currentPage);
                if (!visiblePages.includes(totalPages)) visiblePages.push(totalPages);

                visiblePages.sort((first, second) => first - second);
                visiblePages.forEach((page, index) => {
                    if (index > 0 && page - visiblePages[index - 1] > 1) {
                        html += '<span class="facetwp-page dots" aria-hidden="true">...</span>';
                    }

                    html += '<button class="facetwp-page' + (page === currentPage ? ' active' : '') + '" type="button" data-page="' + page + '"' + (page === currentPage ? ' aria-current="page"' : '') + '>' + page + '</button>';
                });

                html += '<button class="facetwp-page content-repository__pager-arrow next" type="button" aria-label="Next page"';
                html += currentPage < totalPages ?
                    ' data-page="' + (currentPage + 1) + '"' :
                    ' disabled aria-disabled="true"';
                pager.innerHTML = html + '>' + arrow + '</button>';
            });
        };

        const scrollToListing = () => {
            if (!scrollAfterPagination) return;

            const listing = document.querySelector('.content-repository__list');
            scrollAfterPagination = false;

            if (!listing) return;

            const top = listing.getBoundingClientRect().top + window.pageYOffset - 24;
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({
                top: top,
                behavior: reducedMotion ? 'auto' : 'smooth',
            });
        };

        initializeFilters();
        updateSearchPlaceholder();
        updateResultCount();
        updateSort();
        updatePager();
        document.addEventListener('facetwp-loaded', updateSearchPlaceholder);
        document.addEventListener('facetwp-loaded', updateResultCount);
        document.addEventListener('facetwp-loaded', updateSort);
        document.addEventListener('facetwp-loaded', updatePager);
        document.addEventListener('facetwp-loaded', scrollToListing);
        document.addEventListener('click', (event) => {
            const tag = event.target.closest('.content-repository__tag[data-facet][data-value], .content-repository__category[data-facet][data-value]');

            if (tag) {
                const facet = document.querySelector('.facetwp-facet-' + tag.dataset.facet);
                const choice = facet && Array.prototype.find.call(
                    facet.querySelectorAll('[data-value]'),
                    (item) => item.dataset.value === tag.dataset.value
                );

                if (choice && !choice.classList.contains('checked')) choice.click();
            }

            if (event.target.closest('.content-repository__pager .facetwp-page[data-page]')) {
                scrollAfterPagination = true;
            }

            document.querySelectorAll('.content-repository__sort-menu.is-open').forEach((menu) => {
                if (!menu.contains(event.target)) {
                    menu.classList.remove('is-open');
                    menu.querySelector('.content-repository__sort-trigger').setAttribute('aria-expanded', 'false');
                    menu.querySelector('.content-repository__sort-options').hidden = true;
                }
            });
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize);
    } else {
        initialize();
    }
}
